import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';

import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { ConnectionBanner } from '@/components/ConnectionBanner';
import { applyDashboardSettings } from '@/lib/dashboard-settings';
import { queryClient } from '@/lib/query-client';
import { routeTree } from '@/routes';
import { theme } from '@/theme';

const router = createRouter({
    routeTree,
    defaultPreload: 'intent',
    // Sub-200ms perceived load on the order table beats any amount of visual
    // design (PLAN.md §5). Preloading on hover is most of that for free.
    defaultPreloadStaleTime: 0,
    // Back from a parcel returns to the row it was opened from, not to the
    // top of a fifty-row page. Only Back/Forward restore; a new navigation
    // still starts at the top.
    scrollRestoration: true,
});

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}

// Before first render, so the stage grid never paints at the default size and
// then jump to the operator's.
applyDashboardSettings();

const container = document.getElementById('root');

if (container === null) {
    throw new Error('#root is missing from index.html');
}

createRoot(container).render(
    <StrictMode>
        <MantineProvider theme={theme} defaultColorScheme="light">
            <QueryClientProvider client={queryClient}>
                <Notifications position="top-right" />
                <ConnectionBanner />
                <RouterProvider router={router} />
            </QueryClientProvider>
        </MantineProvider>
    </StrictMode>,
);
